import { Component } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { MarineMammalInteractionReport } from '../../data-models/report-models/marine-mammal-interaction-form.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { AuthenticationService } from '../../services/authentication.service';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { VesselData } from 'src/app/data-models/user-models/vessel-data.model';
import { Coordinate } from 'src/app/data-models/coordinate.model';
import { GeolocationMethodsService } from 'src/app/services/geolocation-methods.service';
import { coordValidator, latitudeValidator, longitudeValidator } from 'src/app/data-models/validator.model';
import { MatRadioChange } from '@angular/material/radio';


@Component({
    selector: 'app-marine-mammal-interaction-form',
    templateUrl: './marine-mammal-interaction-form.component.html',
    styleUrls: ['./marine-mammal-interaction-form.component.css'],
    standalone: false
})
export class MarineMammalInteractionFormComponent {

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)
  accidentalContact: boolean = true;
  todayDate: Date = new Date();

  // data preambles
  currTrip: Trip = new Trip();
  userDetails: UserData = new UserData();
  tripReportIndex!: number; //Index of report in the current trip
  marineMammalInteractionReport!: MarineMammalInteractionReport;

  conditionOptions: string[] = ['Appears Healthy', 'Dead', 'Sick', 'Injured', 'Unknown'];
  targetedSpeciesOptions: string[] = ['Salmon, Chinook', 'Salmon, Chum', 'Salmon, Coho', 'Salmon, Pink', 'Salmon, Sockeye'];
  speciesList: string[] = [];
  filteredSpecies: string[] = [];
  provinceOptions: string[] = ['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NB', 'NL', 'NS', 'PE', 'YT', 'NT', 'NU'];
  gearOptions: string[] = ['Gillnet', 'Gillnet (drift)', 'Seines nets (not specified)', 'Troller lines'];
  confidenceOptions: string[] = ['Certain', 'Possible', 'Probable', 'Uncertain'];
  bodyLengthOptions: string[] = ['< 1 m (3 ft)', '1 m - 1.5 m (3–5 ft)', '1.5 m - 2 m (5-7 ft)', '2 m - 3 m (7-10 ft)', '3 m - 8 m (10-25 ft)', '8 m - 16 m (25-50 ft)', '16 m – 26 m (50-80 ft)', '>26 m (> 80 ft)', 'Other'];
  incidentTypeOptions: string[] = ['Collision', 'Dead animal', 'Depredation', 'Entanglement', 'Harassment', 'Live stranding', 'Shooting', 'Sick or injured'];

  // validation check for summary page
  interactionTimeValid: boolean = false;

  isComplete: boolean = false;

  // report data
  marineMammalInteractionForm: UntypedFormGroup = new UntypedFormGroup({
    accidentalContact: new FormControl<boolean>(true, [Validators.required]),  //page 1
    siteDescription: new FormControl<string>('', [Validators.required]),       //page 2
    latitude: new FormControl<Coordinate>(new Coordinate, [coordValidator(), latitudeValidator()]),
    longitude: new FormControl<Coordinate>(new Coordinate, [coordValidator(), longitudeValidator()]),
    fullName: new FormControl<string>('', [Validators.required]),               //page 3
    address: new FormControl<string>('', [Validators.required]),                //page 3
    province: new FormControl<string>('', [Validators.required]),               //page 3
    phoneNum: new FormControl<string>('', [Validators.required]),               //page 3
    email: new FormControl<string>('', [Validators.required]),                  //page 3
    vesselName: new FormControl<string>('', [Validators.required]),             //page 3
    targetedSpecies: new FormControl<string>('', [Validators.required]),        //page 3
    fishingGear: new FormControl<string>('', [Validators.required]),            //page 3
    gearDamaged: new FormControl<boolean>(false, [Validators.required]),        //page 3
    gearLost: new FormControl<boolean>(false, [Validators.required]),           //page 3
    causeKnown: new FormControl<boolean>(false, [Validators.required]),         //page 3
    lostGearRemark: new FormControl<string>('', [Validators.required]),         //page 3
    species: new FormControl<string>('', [Validators.required]),                //page 4
    speciesDescription: new FormControl<string>('', [Validators.required]),     //page 4
    condition: new FormControl<string>('', [Validators.required]),              //page 4
    identificationConfidence: new FormControl<string>('', [Validators.required]),//page 4
    minSpecimens: new FormControl<number>(null!, [                               //page 4
      Validators.required,
      Validators.min(0),
      this.validateMinMaxEstimates.bind(this)]),         // Custom validator                   
    maxSpecimens: new FormControl<number>(null!, [
      Validators.required,
      Validators.min(0),
      this.validateMinMaxEstimates.bind(this)]),         // Custom validator    
    bestEstimateSpecimens: new FormControl<number>(null!, [
      Validators.required,
      Validators.min(0),
      this.validateBestEstimate.bind(this)]),           // Custom validator    
    videosRecorded: new FormControl<boolean>(false, [Validators.required]),     //page 4
    photosTaken: new FormControl<boolean>(false, [Validators.required]),        //page 4
    samplesTaken: new FormControl<boolean>(false, [Validators.required]),       //page 4
    documentsAvailable: new FormControl<boolean>(false, [Validators.required]), //page 4
    documentRemarks: new FormControl<string>('', [Validators.required]),        //page 4
    bodyLengthCategory: new FormControl<string>('', [Validators.required]),     //page 4
    bodyLength: new FormControl<string>(''),                                    //page 4
    eventDescription: new FormControl<string>('', [Validators.required]),       //page 4  
    incidents: new FormArray<FormGroup>([]),                                    //page 5
    remarks: new FormControl<string>('')
  });

  interactionDate: Date = new Date();                                          // page 1
  interactionTime: { hour: number, minute: number } = { hour: 0, minute: 0 };  // page 1

  formUpdateSubscription!: Subscription;

  // page to page
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;

  constructor(
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    public commonDataService: CommonDataService,
    public sharedMethodsService: SharedMethodsService,
    public geolocationMethod: GeolocationMethodsService
  ) {  }

  get incidents() {
    return this.marineMammalInteractionForm.controls["incidents"] as FormArray;
  }

  get incidentControls() {
    return this.incidents.controls as FormGroup[];
  }



  /*  ---------- Report Initialization Methods  ---------- */


  ngOnInit(): void {

    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['marine_mammal_interaction_species_list'];

    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      UserStorageService.getCurrTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.currTrip = UserStorageService.currTrip;
        let reportId = <string>this.route.snapshot.paramMap.get('id');
        this.tripReportIndex = this.currTrip.marineMammalInteractionReports.findIndex((report: MarineMammalInteractionReport) => report.internalId == reportId);
        this.marineMammalInteractionReport = this.currTrip.marineMammalInteractionReports[this.tripReportIndex];
        this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => {
          this.initialize();
        });
      });
    });

  }


  // initialize the data
  initialize(): void {

    this.isComplete = this.route.snapshot.queryParams.status === 'complete';

    if (this.route.snapshot.queryParams.status == 'new') {

      // set the interaction time to the current time
      this.interactionTime = {
        hour: this.interactionDate.getHours(),
        minute: this.interactionDate.getMinutes()
      };

      this.addIncident(); // add an initial incident

    } else {
      this.patchForm()
    }

    this.fillSpecies(); // fill the species list

    // set the default values for the form
    this.marineMammalInteractionForm.controls['fullName'].setValue(this.userDetails.firstName + ' ' + this.userDetails.lastName);
    this.marineMammalInteractionForm.controls['address'].setValue(this.userDetails.address);
    this.marineMammalInteractionForm.controls['phoneNum'].setValue(this.userDetails.phoneNum.toString());
    this.marineMammalInteractionForm.controls['email'].setValue(this.userDetails.userEmail);

    // Subscribe to all input fields that cause changes in other fields on the form    
    this.subscribeToSpeciesChange();
    this.subscribeToBodyLengthCategoryChanges();
    this.subscribeToMinSpecimensChanges();
    this.subscribeToMaxSpecimensChanges();
    this.subscribeToFormChanges();  //subscribe to any change on the form to save updates as they occur

    // Set this default after subscribing to changes so the default data is saved
    this.marineMammalInteractionForm.controls['province'].setValue('BC');

    //If the form is complete (uneditable) set it to view only summary screen
    if (this.isComplete) {
      this.numPages = -1;
      this.currPage = -1;
    } else if (this.marineMammalInteractionForm.get('accidentalContact')?.value) {
      this.numPages = 6;
      this.spinnerIncrement = 100 / this.numPages;
    } else {
      this.clearValidatorsForAccidentalContactFalse();  //clear the required validators for everything except incidents
      this.numPages = 1;
      this.spinnerIncrement = 100;
      this.spinnerProgress = 100;
    }
    this.toNextPage();
  }


  //Populates the form controls with values from the marine mammal interaction report
  patchForm() {

    this.marineMammalInteractionForm.patchValue({
      accidentalContact: this.marineMammalInteractionReport.accidentalContact,
      siteDescription: this.marineMammalInteractionReport.siteDescription,
      latitude: this.marineMammalInteractionReport.latitude,
      longitude: this.marineMammalInteractionReport.longitude,
      fullName: this.marineMammalInteractionReport.fullName,
      address: this.marineMammalInteractionReport.address,
      province: this.marineMammalInteractionReport.province,
      phoneNum: this.marineMammalInteractionReport.phoneNum,
      email: this.marineMammalInteractionReport.email,
      vesselName: this.marineMammalInteractionReport.vesselName,
      targetedSpecies: this.marineMammalInteractionReport.targetedSpecies,
      fishingGear: this.marineMammalInteractionReport.fishingGear,
      gearDamaged: this.marineMammalInteractionReport.gearDamaged,
      gearLost: this.marineMammalInteractionReport.gearLost,
      causeKnown: this.marineMammalInteractionReport.causeKnown,
      lostGearRemark: this.marineMammalInteractionReport.lostGearRemark,
      species: this.marineMammalInteractionReport.species,
      speciesDescription: this.marineMammalInteractionReport.speciesDescription,
      condition: this.marineMammalInteractionReport.condition,
      identificationConfidence: this.marineMammalInteractionReport.identificationConfidence,
      minSpecimens: this.marineMammalInteractionReport.minSpecimens,
      maxSpecimens: this.marineMammalInteractionReport.maxSpecimens,
      bestEstimateSpecimens: this.marineMammalInteractionReport.bestEstimateSpecimens,
      videosRecorded: this.marineMammalInteractionReport.videosRecorded,
      photosTaken: this.marineMammalInteractionReport.photosTaken,
      samplesTaken: this.marineMammalInteractionReport.samplesTaken,
      documentsAvailable: this.marineMammalInteractionReport.documentsAvailable,
      documentRemarks: this.marineMammalInteractionReport.documentRemarks,
      bodyLengthCategory: this.marineMammalInteractionReport.bodyLengthCategory,
      bodyLength: this.marineMammalInteractionReport.bodyLength,
      eventDescription: this.marineMammalInteractionReport.eventDescription,
      remarks: this.marineMammalInteractionReport.remarks
    });
    this.interactionDate = this.marineMammalInteractionReport.interactionDate;
    this.interactionTime = { hour: this.marineMammalInteractionReport.interactionDate.getHours(), minute: this.marineMammalInteractionReport.interactionDate.getMinutes() };

    // Patch the FormArray (incidents) if it exists in the marineMammalInteractionReport
    if (this.marineMammalInteractionReport.incidents && this.marineMammalInteractionReport.incidents.length > 0) {
      const incidentsArray = this.marineMammalInteractionForm.get('incidents') as FormArray;

      // Clear existing FormArray
      incidentsArray.clear();

      // Patch values for each incident in the FormArray
      this.marineMammalInteractionReport.incidents.forEach(incident => {
        const incidentFormGroup = new FormGroup({
          type: new FormControl(incident.type, [Validators.required]),
          description: new FormControl(incident.description, [Validators.required]),
        });

        incidentsArray.push(incidentFormGroup);
      });
    }
  }


  // Subscribe to all changes in the form to update and save as changes occur
  private subscribeToFormChanges(): void {

    this.formUpdateSubscription = this.marineMammalInteractionForm.valueChanges.subscribe(form => {
      const interactionReport = this.currTrip.marineMammalInteractionReports[this.tripReportIndex];

      interactionReport.accidentalContact = form.accidentalContact;
      interactionReport.siteDescription = form.siteDescription;
      interactionReport.latitude = form.latitude;
      interactionReport.longitude = form.longitude;
      interactionReport.species = form.species;
      interactionReport.fullName = form.fullName;
      interactionReport.address = form.address;
      interactionReport.province = form.province;
      interactionReport.phoneNum = form.phoneNum;
      interactionReport.email = form.email;
      interactionReport.vesselName = form.vesselName;
      interactionReport.targetedSpecies = form.targetedSpecies;
      interactionReport.fishingGear = form.fishingGear;
      interactionReport.gearDamaged = form.gearDamaged;
      interactionReport.gearLost = form.gearLost;
      interactionReport.causeKnown = form.causeKnown;
      interactionReport.lostGearRemark = form.lostGearRemark;
      interactionReport.species = form.species;
      interactionReport.speciesDescription = form.speciesDescription;
      interactionReport.condition = form.condition;
      interactionReport.identificationConfidence = form.identificationConfidence;
      interactionReport.minSpecimens = form.minSpecimens;
      interactionReport.maxSpecimens = form.maxSpecimens;
      interactionReport.bestEstimateSpecimens = form.bestEstimateSpecimens;
      interactionReport.videosRecorded = form.videosRecorded;
      interactionReport.photosTaken = form.photosTaken;
      interactionReport.samplesTaken = form.samplesTaken;
      interactionReport.documentsAvailable = form.documentsAvailable;
      interactionReport.documentRemarks = form.documentRemarks;
      interactionReport.bodyLengthCategory = form.bodyLengthCategory;
      interactionReport.bodyLength = form.bodyLength;
      interactionReport.eventDescription = form.eventDescription;
      interactionReport.remarks = form.remarks;

      const incidentsArray = this.marineMammalInteractionForm.get('incidents') as FormArray;
      if (incidentsArray) {
        interactionReport.incidents = incidentsArray.value;
      }

      UserStorageService.saveTrip(this.currTrip, true, false);
    });
  }


  //Updates the interaction date and time for a specific marine mammal interaction report, then saves the modified trip to user storage.
  dateTimeChange() {

    this.currTrip.marineMammalInteractionReports[this.tripReportIndex].interactionDate = this.interactionDate;
    this.currTrip.marineMammalInteractionReports[this.tripReportIndex].interactionDate.setMinutes(this.interactionTime.minute);
    this.currTrip.marineMammalInteractionReports[this.tripReportIndex].interactionDate.setHours(this.interactionTime.hour);
    UserStorageService.saveTrip(this.currTrip, true, false);

  }


  // Changes the form between the the variants depending on if contact occured between a marine mammal and a vessel or fishing gear
  accidentalContactChanged(event: MatRadioChange): void {

    const isChecked = event.value;
    if (!isChecked) {
      this.numPages = 1;
      this.spinnerIncrement = 100;
      this.spinnerProgress = 100;
      this.clearValidatorsForAccidentalContactFalse();
    } else {
      this.numPages = 6;
      this.spinnerIncrement = 100 / this.numPages;
      this.spinnerProgress = this.spinnerIncrement;
      this.setValidatorsForAccidentalContactTrue();
    }
  }


  addIncident() {

    this.incidents.push(new FormGroup({
      type: new FormControl<string>('', [Validators.required]),
      description: new FormControl<string>('', [Validators.required])
    }));
    this.incidents.updateValueAndValidity({ emitEvent: false });

  }


  deleteIncident(index: number) {
    this.incidents.removeAt(index);
  }


  // fill up the species list
  fillSpecies(): void { // list of species gets passed in here

    for (let species of CommonDataService.marineMammalInteractionSpeciesList) { // get all species names that are already in the table
      this.speciesList.push(species.speciesName);
    }
    this.filteredSpecies = this.speciesList; // fill the filtered list
    this.speciesList.sort(); //sort species list alphabetically

  }

  // change the filtered species list whenever the input field changes
  subscribeToSpeciesChange(): void {

    this.marineMammalInteractionForm.get('species')!.valueChanges.subscribe((speciesChange: string) => {
      this.filteredSpecies = [];
      for (let species of this.speciesList) {
        if (species.toLowerCase().includes(speciesChange.toLowerCase())) {
          this.filteredSpecies.push(species);
        }
      }
    });

  }

  compareVessel(v1: VesselData, v2: VesselData): boolean {
    return v1 && v2 ? v1.vesselName+v1.vesselRegistrationNumber === v2.vesselName+v2.vesselRegistrationNumber : v1 === v2;
  }


  /* ---------- Page Navigation Methods ---------- */


  // check current page before going to the next one
  toNextPage(): void {

    if (this.currPage <= this.numPages) {
      if (this.currPage === this.numPages) {
        this.setSummaryPage();
      }
      this.currPage++;
      this.spinnerProgress += this.spinnerIncrement;
    }
    else {
      this.saveReport();
    }
  }


  // back one page
  toPreviousPage(): void {

    if (this.currPage <= 1) {

      this.sharedMethodsService.toHome(this.userDetails.currHomepage);

    }

    this.currPage--;
    this.spinnerProgress -= this.spinnerIncrement;
  }

  // set all of the data to display on the summary based on the entered report fields
  setSummaryPage(): void {

    // set validation values
    this.interactionTimeValid = !!this.interactionTime


  }


  /* ---------- Helper Methods ---------- */

  // clear validators if accidentalContact answer is no
  private clearValidatorsForAccidentalContactFalse(): void {
    const controlsToClearValidators = [
      'siteDescription', 'latitude', 'longitude', 'fullName', 'address', 'province',
      'phoneNum', 'email', 'vesselName', 'targetedSpecies', 'fishingGear',
      'gearDamaged', 'gearLost', 'causeKnown', 'lostGearRemark', 'species',
      'speciesDescription', 'condition', 'identificationConfidence', 'minSpecimens',
      'maxSpecimens', 'bestEstimateSpecimens', 'videosRecorded', 'photosTaken',
      'samplesTaken', 'documentsAvailable', 'documentRemarks', 'bodyLengthCategory',
      'bodyLength', 'eventDescription'
    ];

    controlsToClearValidators.forEach(controlName => {
      const control = this.marineMammalInteractionForm.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }


  // set required validators if accidentalContact answer is yes
  private setValidatorsForAccidentalContactTrue(): void {
    const controlsToSetValidators = [
      'siteDescription', 'latitude', 'longitude', 'fullName', 'address', 'province',
      'phoneNum', 'email', 'vesselName', 'targetedSpecies', 'fishingGear',
      'gearDamaged', 'gearLost', 'causeKnown', 'lostGearRemark', 'species',
      'speciesDescription', 'condition', 'identificationConfidence', 'minSpecimens',
      'maxSpecimens', 'bestEstimateSpecimens', 'videosRecorded', 'photosTaken',
      'samplesTaken', 'documentsAvailable', 'documentRemarks', 'bodyLengthCategory',
      'bodyLength', 'eventDescription'
    ];

    controlsToSetValidators.forEach(controlName => {

      const control = this.marineMammalInteractionForm.get(controlName);

      switch (controlName) {
        case 'latitude':
          control?.setValidators([coordValidator(), latitudeValidator()]);
          break;

        case 'longitude':
          control?.setValidators([coordValidator(), longitudeValidator()]);
          break;

        case 'minSpecimens':
        case 'maxSpecimens':
        case 'bestEstimateSpecimens':
          control?.setValidators([
            Validators.required,
            Validators.min(0),
            (controlName === 'bestEstimateSpecimens') ? this.validateBestEstimate.bind(this) : this.validateMinMaxEstimates.bind(this)
          ]);
          break;

        case 'lostGearRemark':
          // Set required validator only if 'causeKnown' is true
          const causeKnownControl = this.marineMammalInteractionForm.get('causeKnown');
          if (causeKnownControl?.value) {
            control?.setValidators([Validators.required]);
          } else {
            control?.clearValidators();
          }
          break;

        case 'documentRemarks':
          // Set required validator only if 'documentsAvailable' is true
          const documentsAvailableControl = this.marineMammalInteractionForm.get('documentsAvailable');
          if (documentsAvailableControl?.value) {
            control?.setValidators([Validators.required]);
          } else {
            control?.clearValidators();
          }
          break;

        case 'bodyLength':
          // Set required validator only if 'bodyLengthCategory' is 'other'
          const bodyLengthCategoryControl = this.marineMammalInteractionForm.get('bodyLengthCategory');
          if (bodyLengthCategoryControl?.value === 'Other') {
            control?.setValidators([Validators.required]);
          } else {
            control?.clearValidators();
          }
          break;

        default:
          // For other controls, just set the required validator
          control?.setValidators([Validators.required]);
          break;
      }

      control?.updateValueAndValidity({ emitEvent: false });
    });
  }


  // returns the current date with the current start fishing time, the returned date should only be used to read time
  getInteractionTime(): Date {

    let tempDate: Date = new Date();
    this.sharedMethodsService.setDateTime(tempDate, this.interactionTime);

    return tempDate;

  }

  // Subscribe to changes in the bodyLengthCategory control and update the validator for bodyLength
  private subscribeToBodyLengthCategoryChanges(): void {
    const bodyLengthCategoryControl = this.marineMammalInteractionForm.get('bodyLengthCategory');
    const bodyLengthControl = this.marineMammalInteractionForm.get('bodyLength');

    if (!bodyLengthCategoryControl || !bodyLengthControl) {
      return; // Return early if controls are null
    }

    bodyLengthCategoryControl.valueChanges.subscribe((category) => {
      if (category === 'Other') {
        bodyLengthControl.setValidators([Validators.required]);
      } else {
        bodyLengthControl.clearValidators();
      }

      bodyLengthControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  // Subscribe to changes in the minSpecimens control to allow error messages for the other controls to also update
  private subscribeToMinSpecimensChanges(): void {

    let minSpecimensControl = this.marineMammalInteractionForm.get('minSpecimens');

    minSpecimensControl?.valueChanges.subscribe(() => {
      this.marineMammalInteractionForm.get('maxSpecimens')?.updateValueAndValidity({ emitEvent: false });
      this.marineMammalInteractionForm.get('bestEstimateSpecimens')?.updateValueAndValidity({ emitEvent: false });
    });
  }
  // Subscribe to changes in the maxSpecimens control to allow error messages for the other controls to also update
  private subscribeToMaxSpecimensChanges(): void {

    let maxSpecimensControl = this.marineMammalInteractionForm.get('maxSpecimens');

    maxSpecimensControl?.valueChanges.subscribe(() => {
      this.marineMammalInteractionForm.get('minSpecimens')?.updateValueAndValidity({ emitEvent: false });
      this.marineMammalInteractionForm.get('bestEstimateSpecimens')?.updateValueAndValidity({ emitEvent: false });
    });
  }


  /* ---------- Custom Validator Methods ---------- */


  validateMinMaxEstimates(control: AbstractControl): ValidationErrors | null {
    const minSpecimens = control.root.get('minSpecimens')?.value;
    const maxSpecimens = control.root.get('maxSpecimens')?.value;

    if (minSpecimens !== null && maxSpecimens !== null && minSpecimens > maxSpecimens) {
      return { minMaxError: true };
    }

    return null;
  }

  validateBestEstimate(control: AbstractControl): ValidationErrors | null {

    const bestEstimate = control.value;
    const minSpecimens = control.root.get('minSpecimens')?.value;
    const maxSpecimens = control.root.get('maxSpecimens')?.value;

    if (bestEstimate !== null && (bestEstimate < minSpecimens || bestEstimate > maxSpecimens)) {
      return { bestEstimateError: true };
    }

    return null;
  }


  /* ---------- Report Completion Methods ---------- */


  // Placeholder, save and transmission will be added later
  saveReport(): void {

    if (this.marineMammalInteractionForm.valid && this.interactionTimeValid) {
      //this.isReportSending = true;
      this.saveReportData(); // save report data
      this.marineMammalInteractionReport.isSubmitted = true;
      UserStorageService.saveTrip(this.currTrip).then(() => {
        // navigate to the home page
        this.sharedMethodsService.toHome(this.userDetails.currHomepage);
        this.sharedMethodsService.toastSavedSuccessfully();
      });
      // transmission methods will go here
    }
    else {
      this.sharedMethodsService.toastInvalidForm();
    }

  }


  // saves report data using the form data collected
  saveReportData(): void {

    // save relevant information gathered in the form

    if (this.marineMammalInteractionForm.get('accidentalContact')?.value) {
      this.marineMammalInteractionReport.interactionDate = this.interactionDate;
      this.marineMammalInteractionReport.accidentalContact = this.marineMammalInteractionForm.get('accidentalContact')?.value;
      this.marineMammalInteractionReport.siteDescription = this.marineMammalInteractionForm.get('siteDescription')?.value;
      this.marineMammalInteractionReport.latitude = this.marineMammalInteractionForm.get('latitude')?.value;
      this.marineMammalInteractionReport.longitude = this.marineMammalInteractionForm.get('longitude')?.value;
      this.marineMammalInteractionReport.fullName = this.marineMammalInteractionForm.get('fullName')?.value;
      this.marineMammalInteractionReport.address = this.marineMammalInteractionForm.get('address')?.value;
      this.marineMammalInteractionReport.province = this.marineMammalInteractionForm.get('province')?.value;
      this.marineMammalInteractionReport.phoneNum = this.marineMammalInteractionForm.get('phoneNum')?.value;
      this.marineMammalInteractionReport.email = this.marineMammalInteractionForm.get('email')?.value;
      this.marineMammalInteractionReport.vesselName = this.marineMammalInteractionForm.get('vesselName')?.value;
      this.marineMammalInteractionReport.targetedSpecies = this.marineMammalInteractionForm.get('targetedSpecies')?.value;
      this.marineMammalInteractionReport.fishingGear = this.marineMammalInteractionForm.get('fishingGear')?.value;
      this.marineMammalInteractionReport.gearDamaged = this.marineMammalInteractionForm.get('gearDamaged')?.value;
      this.marineMammalInteractionReport.gearLost = this.marineMammalInteractionForm.get('gearLost')?.value;
      this.marineMammalInteractionReport.causeKnown = this.marineMammalInteractionForm.get('causeKnown')?.value;
      this.marineMammalInteractionReport.lostGearRemark = this.marineMammalInteractionForm.get('lostGearRemark')?.value;
      this.marineMammalInteractionReport.species = this.marineMammalInteractionForm.get('species')?.value;
      this.marineMammalInteractionReport.speciesDescription = this.marineMammalInteractionForm.get('speciesDescription')?.value;
      this.marineMammalInteractionReport.condition = this.marineMammalInteractionForm.get('condition')?.value;
      this.marineMammalInteractionReport.identificationConfidence = this.marineMammalInteractionForm.get('identificationConfidence')?.value;
      this.marineMammalInteractionReport.minSpecimens = this.marineMammalInteractionForm.get('minSpecimens')?.value;
      this.marineMammalInteractionReport.maxSpecimens = this.marineMammalInteractionForm.get('maxSpecimens')?.value;
      this.marineMammalInteractionReport.bestEstimateSpecimens = this.marineMammalInteractionForm.get('bestEstimateSpecimens')?.value;
      this.marineMammalInteractionReport.videosRecorded = this.marineMammalInteractionForm.get('videosRecorded')?.value;
      this.marineMammalInteractionReport.photosTaken = this.marineMammalInteractionForm.get('photosTaken')?.value;
      this.marineMammalInteractionReport.samplesTaken = this.marineMammalInteractionForm.get('samplesTaken')?.value;
      this.marineMammalInteractionReport.documentsAvailable = this.marineMammalInteractionForm.get('documentsAvailable')?.value;
      this.marineMammalInteractionReport.documentRemarks = this.marineMammalInteractionForm.get('documentRemarks')?.value;
      this.marineMammalInteractionReport.bodyLengthCategory = this.marineMammalInteractionForm.get('bodyLengthCategory')?.value;
      this.marineMammalInteractionReport.bodyLength = this.marineMammalInteractionForm.get('bodyLength')?.value;
      this.marineMammalInteractionReport.eventDescription = this.marineMammalInteractionForm.get('eventDescription')?.value;
    }
    
    this.marineMammalInteractionReport.reportId = SharedMethodsService.generateReportID("");

    // Handling incidents FormArray
    const incidentsArray = this.marineMammalInteractionForm.get('incidents') as FormArray;
    if (incidentsArray) {
      this.marineMammalInteractionReport.incidents = incidentsArray.controls.map((incident: AbstractControl) => {
        return {
          type: incident.get('type')?.value,
          description: incident.get('description')?.value,
        };
      });
    }

  }


  deleteReport() {
    this.dialog.open(ConfirmationDialogComponent, {
      data: { screen: 'delete_report' }
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.deleteTripReport((trip) => trip.marineMammalInteractionReports, this.marineMammalInteractionReport.internalId)
          .then(() => this.sharedMethodsService.toHome(this.userDetails.currHomepage));
      }
    });
  }

  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }

}
